// components/public/TaxRemindersForm.tsx
// Quarterly tax reminder signup form (interactive). Split out of
// LeadCapture.tsx so it ships only on pages that mount it. Copy is final,
// from Grok Bot, and is unchanged.
//
// This is a client component for form interactivity.

"use client";

import { useRef, useState, useSyncExternalStore } from "react";
import { ArrowRight } from "lucide-react";
import type { LeadSource } from "@/lib/lead-sources";
import {
  getLeadReturnFlag,
  getLeadReturnFlagServer,
  subscribeLeadReturnFlag,
} from "@/components/public/lead-return-flag";

// Quarterly tax reminder signup (R4-A). Copy is final, from Grok Bot; do not
// edit it here. Mounted only while TAX_REMINDERS_SIGNUP_ENABLED is on
// (lib/tax-reminders.ts). Posts topic "tax-reminders" to /api/leads; the
// hidden topic input mirrors it for the no-JS path. No GA4 funnel events.
export default function TaxRemindersForm({ source }: { source?: LeadSource }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const returnFlag = useSyncExternalStore(
    subscribeLeadReturnFlag,
    getLeadReturnFlag,
    getLeadReturnFlagServer,
  );
  const visibleStatus = status === "idle" && returnFlag ? returnFlag : status;
  const emailInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        redirect: "error",
        body: JSON.stringify({ email, source, topic: "tax-reminders" }),
      });
      const contentType = res.headers.get("content-type") ?? "";
      if (!contentType.includes("application/json")) throw new Error("bad response");
      const body = (await res.json()) as { ok?: boolean };
      if (!res.ok || !body.ok) throw new Error("bad response");
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
      emailInputRef.current?.focus();
    }
  };

  if (visibleStatus === "success") {
    return (
      <section
        aria-labelledby="tax-reminders-heading"
        className="relative overflow-hidden rounded-2xl border border-forge-trust/20 bg-content-surface p-8 sm:p-12"
      >
        <h2 id="tax-reminders-heading" className="sr-only">
          Get a reminder before each quarterly tax deadline
        </h2>
        <p role="status" className="text-content-text leading-relaxed">
          You&apos;re on the list. The first reminder arrives about a week before January 15, 2027.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="tax-reminders-heading"
      className="relative overflow-hidden rounded-2xl border border-content-border bg-content-surface p-8 sm:p-12"
    >
      <h2
        id="tax-reminders-heading"
        className="text-2xl sm:text-3xl font-bold text-content-text mb-3 tracking-tight"
      >
        Get a reminder before each quarterly tax deadline
      </h2>
      <p className="text-content-muted leading-relaxed mb-5">
        Self-employed or earning on the side? We&apos;ll email you about a week before each estimated tax due date, starting with January 15, 2027, with a link to the calculator so you can check the amount.
      </p>
      <form
        onSubmit={handleSubmit}
        method="post"
        action="/api/leads"
        className="flex flex-col gap-3 lg:max-w-xl"
      >
        {visibleStatus === "error" && (
          <p id="tax-reminders-error" className="text-base text-forge-urgency" role="alert">
            That didn&apos;t go through. Check the address and try again.
          </p>
        )}
        <label htmlFor="tax-reminders-email" className="text-base font-semibold text-content-text">
          Email address
        </label>
        <input type="hidden" name="source" value={source ?? "unknown"} />
        <input type="hidden" name="topic" value="tax-reminders" />
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            ref={emailInputRef}
            id="tax-reminders-email"
            name="email"
            type="email"
            required
            aria-invalid={visibleStatus === "error" ? "true" : undefined}
            aria-describedby={visibleStatus === "error" ? "tax-reminders-error" : undefined}
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-lg border border-content-border bg-content-bg px-4 py-3.5 text-base text-content-text placeholder:text-content-muted outline-none transition-colors focus:border-forge-trust/40 focus:ring-1 focus:ring-forge-trust/20"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="flex items-center justify-center gap-2 rounded-lg bg-forge-trust px-6 py-3.5 text-base font-semibold text-content-bg transition-all duration-300 hover:bg-forge-trust-hover disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {status === "loading" ? (
              "Sending..."
            ) : (
              <>
                Send me the reminders
                <ArrowRight size={16} aria-hidden="true" />
              </>
            )}
          </button>
        </div>
        <p data-nosnippet className="text-base leading-snug text-content-muted">
          Four emails a year, one before each due date. Every email has a one-click unsubscribe link. We don&apos;t sell or share your address.
        </p>
      </form>
    </section>
  );
}
